import { apiError } from "../../../../../utils/formErrors";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack, TextField, CircularProgress } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useNavigate } from "react-router";
import TopBar from "../../../../../component/TopBar";
import MainBox from "../../../container/MainBox";
import SectionLoader from "../../../../../component/SectionLoader";
import EmptyState from "../../../../../component/EmptyState";
import BtnPrimary from "../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import Counter from "../../../../../component/Counter";
import InlineMessage from "../../../../../component/InlineMessage";
import { StatusChip } from "../../../../../component/listing";
import { counterRowSx } from "../../../container/frameLayout";
import { useNotify } from "../../../../../component/NotificationProvider";
import { useSelfEmployee } from "../../../../../services/useOrgData";
import { getMyGoals, updateGoal, getMyReviews } from "../../../../../services/performance";
import SelfReviewModal from "./SelfReviewModal";
import { tokens, radius, displayFont } from "../../../../../theme";
import {
  goalProgress, goalsInCycle, goalTotals, cycleFor,
  cycleLine, lastFinalisedReview, currentReview,
  checkInOf, targetLabel, CHECK_IN, CHECK_IN_OPTIONS,
} from "./selfGoals";

/**
 * An employee's own goals, and where they stand in the cycle around them.
 *
 * Was a flat list of goal cards. The list was correct — progress is the only
 * thing editable here, because what the goal *is* is the agreement with a
 * manager — but it answered only "what are my goals" and never "what is
 * happening with them". Somebody looking at four goals at 60% cannot tell from
 * that whether their self-review is due tomorrow or the cycle closed in March.
 *
 * So the rail across the top is the actual addition: five stages, exactly one
 * of them lit, driven by the review's own status.
 *
 * Two things the design book asks for are deliberately absent, both because the
 * data behind them does not exist rather than because they were skipped:
 *
 *   - "At risk", a flag the employee raises on a goal. The goal status enum has
 *     no such value. Deriving it from "deadline near, progress low" would put a
 *     warning on somebody's record they never made and cannot clear.
 *   - "Propose a goal". An employee-created goal would be live immediately —
 *     there is no draft-pending-agreement state — which inverts the book's own
 *     rule that a proposed goal stays a draft until the manager agrees it, and
 *     would let a goal count toward an appraisal nobody agreed to.
 */

const STATUS_TONE = { not_started: "muted", in_progress: "info", completed: "success", cancelled: "danger" };
const STATUS_LABEL = { not_started: "Not started", in_progress: "In progress", completed: "Completed", cancelled: "Cancelled" };

const Section = ({ children }) => (
  <Box sx={{ fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: tokens.primary, mb: "1rem" }}>
    {children}
  </Box>
);

/** The goal's own progress bar. Green once met, so a finished goal reads as finished. */
const Bar = ({ value, met }) => (
  <Box sx={{ height: "8px", borderRadius: "4px", background: tokens.border, overflow: "hidden" }}>
    <Box
      sx={{
        width: `${Math.max(0, Math.min(100, value))}%`,
        height: "100%",
        background: met ? tokens.successText : tokens.primary,
        transition: "width 200ms cubic-bezier(0.2, 0, 0.2, 1)",
      }}
    />
  </Box>
);

const MyGoals = () => {
  const notify = useNotify();
  const navigate = useNavigate();
  const { data: self } = useSelfEmployee();

  const [goals, setGoals] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // goalId -> { krIndex: value }
  // goalId -> { state, note }, held separately because a check-in can be edited
  // on a goal with no key results at all
  const [checkDrafts, setCheckDrafts] = useState({});
  const [reviewOpen, setReviewOpen] = useState(false);
  const [saving, setSaving] = useState(null);
  // which goal has its key-result editor open
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    // Only the goals matter enough to fail on. The reviews decorate the screen;
    // without them it degrades to the list it used to be, which is still usable.
    // The cycle is read off the reviews rather than fetched — `/review-cycle` is
    // an admin endpoint, so asking for it here would 403 for every employee.
    const [goalRes, reviewRes] = await Promise.all([
      getMyGoals().catch(() => null),
      getMyReviews().catch(() => null),
    ]);
    setGoals(Array.isArray(goalRes?.data) ? goalRes.data : []);
    setReviews(Array.isArray(reviewRes?.data) ? reviewRes.data : []);
    setDrafts({});
    setCheckDrafts({});
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const cycle = useMemo(() => cycleFor(reviews, goals), [reviews, goals]);
  const review = useMemo(() => currentReview(reviews), [reviews]);
  const manager = self?.reportingManager;

  // The cycle's own goals lead. A goal from a previous cycle is still the
  // employee's, but it is not what this cycle is judged on.
  const cycleGoals = useMemo(() => goalsInCycle(goals, cycle?._id), [goals, cycle]);
  const shown = cycleGoals.length ? cycleGoals : goals;

  const totals = useMemo(() => goalTotals(shown), [shown]);
  const previous = useMemo(() => lastFinalisedReview(reviews, cycle?._id), [reviews, cycle]);

  const setDraft = (goalId, i, value) =>
    setDrafts((p) => ({ ...p, [goalId]: { ...(p[goalId] || {}), [i]: value } }));

  const setCheckDraft = (goalId, next) =>
    setCheckDrafts((p) => ({ ...p, [goalId]: next.state, [`${goalId}:note`]: next.note }));

  // The note in hand: the unsaved edit if there is one, else what was saved.
  const noteFor = (goal) => {
    const draft = checkDrafts[`${goal._id}:note`];
    return draft !== undefined ? draft : goal?.checkIn?.note || "";
  };

  const save = async (goal) => {
    const edits = drafts[goal._id];
    const checkEdited = checkDrafts[goal._id] !== undefined;
    if (!edits && !checkEdited) return;
    setSaving(goal._id);
    try {
      const payload = {};

      if (edits) {
        // send the whole array; the server keeps title/target/unit and takes only
        // current from it, so a stale definition here cannot overwrite the real one
        payload.keyResults = (goal.keyResults || []).map((k, i) => ({
          ...k,
          current: edits[i] !== undefined ? Number(edits[i]) || 0 : k.current,
        }));
      }

      /*
       * `progress` and `status` are never sent. Progress is calculated from the
       * key results server-side and hand-setting it is refused; status follows
       * from progress. Sending either would be asking the server to take our
       * word for the one figure the appraisal treats as evidence.
       */
      if (checkEdited) {
        const state = checkDrafts[goal._id];
        payload.checkIn = state ? { state, note: noteFor(goal) } : null;
      }

      const res = await updateGoal(goal._id, payload);
      if (res?.success === false) throw apiError(res, "Could not save");
      notify.success("Saved");
      setEditing(null);
      setCheckDrafts({});
      await load();
    } catch (err) {
      notify.error(err?.message || "Could not save");
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: "100%" }}>
        <TopBar MainHeading="Goals" />
        <SectionLoader minHeight="50vh" />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar
        MainHeading="Performance"
        breadcrumbs={[{ label: "Performance" }]}
        meta={cycleLine({ cycle, review, manager })}
        actions={
          review && review.status === "pending" ? (
            <BtnPrimary sx={{ borderRadius: "8px" }} onClick={() => setReviewOpen(true)}>
              {(review.self?.answers || []).length ? "Continue your self-review" : "Start your self-review"}
            </BtnPrimary>
          ) : null
        }
      />

      <Box sx={{ ...counterRowSx(4), mb: "1.25rem" }}>
        <Counter
          label="Goals this cycle"
          value={totals.total}
          sub={totals.cancelled ? `${totals.cancelled} cancelled, not counted` : cycle?.name || "across every cycle"}
        />
        <Counter
          label="Completed"
          value={totals.completed}
          sub={totals.total ? `of ${totals.total}` : "none set"}
          tone={totals.completed ? tokens.successText : undefined}
        />
        <Counter label="In progress" value={totals.inProgress} sub="under way" />
        <Counter
          label="Not started"
          value={totals.notStarted}
          sub={totals.notStarted ? "no progress recorded yet" : "all under way"}
          tone={totals.notStarted ? tokens.warningText : undefined}
        />
      </Box>

      {shown.length > 0 && (
        <InlineMessage tone="info" sx={{ mb: "1.25rem" }}>
          Completed is calculated from your key results, not set by hand, so it is
          something your manager can check rather than take on trust. How a goal is
          <em> going</em> is yours to say and nothing infers it — no date marks your
          work at risk on your behalf.
        </InlineMessage>
      )}

      {shown.length === 0 ? (
        <EmptyState
          title="No goals set"
          description="Goals you agree with your manager appear here, and you update your progress against them."
        />
      ) : (
        <Stack spacing={2}>
          {shown.map((g) => {
            const lock = g.locked || {};
            // A cancelled goal is history: it keeps its place in the record so
            // what was agreed stays visible, but nothing about it moves again.
            const cancelled = g.status === "cancelled";
            const readOnly = Boolean(lock.progress) || cancelled;
            const edits = drafts[g._id] || {};
            const draftCheck = checkDrafts[g._id];
            const dirty = Object.keys(edits).length > 0 || draftCheck !== undefined;
            const open = editing === g._id;
            const pct = goalProgress(g);
            const met = g.status === "completed";
            const krs = g.keyResults || [];
            const check = checkInOf(g);
            const target = targetLabel(g);

            return (
              <Box
                key={g._id}
                sx={{
                  border: `1px solid ${open ? tokens.borderStrong : tokens.border}`,
                  borderRadius: `${radius.card}px`,
                  background: tokens.card,
                  p: "1.5rem",
                  opacity: cancelled ? 0.72 : 1,
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={1.75} sx={{ flexWrap: "wrap", mb: "0.875rem" }}>
                  <Box sx={{ flex: "1 1 16rem", minWidth: 0 }}>
                    <Box
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 600,
                        color: tokens.text,
                        mb: "0.25rem",
                        textDecoration: cancelled ? "line-through" : "none",
                      }}
                    >
                      {g.objective}
                    </Box>
                    <Box sx={{ fontSize: "0.8125rem", color: tokens.primary }}>
                      {[g.reviewCycle?.name, target, `weight ${g.weight ?? 1}`].filter(Boolean).join(" · ")}
                    </Box>
                  </Box>
                  {/* Two different claims, side by side and never merged: what
                      the key results prove, and what the employee says about how
                      it is going. */}
                  {check && <StatusChip label={check.label} tone={check.tone} />}
                  <StatusChip label={STATUS_LABEL[g.status] || g.status} tone={STATUS_TONE[g.status] || "muted"} />
                  <Box
                    sx={{
                      fontFamily: displayFont,
                      fontSize: "1.25rem",
                      fontWeight: 700,
                      fontVariantNumeric: "tabular-nums",
                      width: "3.5rem",
                      textAlign: "right",
                      flex: "none",
                      color: tokens.text,
                    }}
                  >
                    {pct}%
                  </Box>
                </Stack>

                <Bar value={pct} met={met} />

                <Stack direction="row" alignItems="center" spacing={1.75} sx={{ flexWrap: "wrap", mt: "1rem" }}>
                  <Box sx={{ flex: "1 1 14rem", minWidth: 0, fontSize: "0.8125rem", color: tokens.textMuted, lineHeight: 1.6 }}>
                    {check?.note || g.description || (
                      <Box component="span" sx={{ color: tokens.textPlaceholder }}>No update yet</Box>
                    )}
                  </Box>
                  {readOnly ? (
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flex: "none", fontSize: "0.75rem", color: tokens.textMuted }}>
                      <LockOutlinedIcon sx={{ fontSize: "0.9375rem" }} />
                      {/* the reason matters as much as the lock: a control that
                          stops working without saying why reads as broken */}
                      <Box>{cancelled ? "Cancelled goals are read-only" : lock.reason || "This cycle is closed"}</Box>
                    </Stack>
                  ) : (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setEditing(open ? null : g._id)}
                      aria-expanded={open}
                      sx={{
                        font: "inherit",
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        color: tokens.primary,
                        background: "transparent",
                        border: 0,
                        p: 0,
                        flex: "none",
                        cursor: "pointer",
                        "&:focus-visible": { outline: `2px solid ${tokens.accent}`, outlineOffset: "2px" },
                      }}
                    >
                      {open ? "Done" : "Update"}
                    </Box>
                  )}
                </Stack>

                {open && !readOnly && (
                  <Box sx={{ mt: "1.125rem", pt: "1.125rem", borderTop: `1px solid ${tokens.border}` }}>
                    {/* The employee's half. Deliberately first: it is the thing
                        only they can supply, where the measures below are a
                        transcription of work already done. */}
                    <Section>How is it going?</Section>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: "1.5rem" }}>
                      <TextField
                        select
                        size="small"
                        label="Your read"
                        value={(draftCheck ?? check?.state) || ""}
                        onChange={(e) => setCheckDraft(g._id, { state: e.target.value, note: noteFor(g) })}
                        sx={{ minWidth: "11rem", "& .MuiInputBase-root": { fontSize: "0.875rem", borderRadius: "8px" } }}
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                      >
                        {/* Clearing is a real choice: withdrawing a flag is not
                            the same as claiming everything is fine. */}
                        <option value="">Not said</option>
                        {CHECK_IN_OPTIONS.map((k) => (
                          <option key={k} value={k}>{CHECK_IN[k].label}</option>
                        ))}
                      </TextField>
                      <TextField
                        size="small"
                        multiline
                        minRows={2}
                        label="What has changed"
                        placeholder="Optional. A sentence your manager can read before your review."
                        value={noteFor(g)}
                        onChange={(e) => setCheckDraft(g._id, { state: (draftCheck ?? check?.state) || "", note: e.target.value })}
                        sx={{ flex: 1, "& .MuiInputBase-root": { fontSize: "0.875rem", borderRadius: "8px" } }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Stack>

                    <Section>Where each measure stands</Section>
                    {krs.length === 0 && (
                      <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "0.75rem" }}>
                        This goal carries no key results, so there is nothing to report against.
                      </Box>
                    )}
                    <Stack spacing={1.25}>
                      {krs.map((k, i) => (
                        <Stack key={k._id || i} direction="row" alignItems="center" spacing={1.25} sx={{ flexWrap: "wrap" }}>
                          <Box sx={{ flex: "1 1 12rem", fontSize: "0.875rem", color: tokens.text, minWidth: 0 }}>
                            {k.title}
                          </Box>
                          <TextField
                            size="small"
                            type="number"
                            value={edits[i] !== undefined ? edits[i] : k.current ?? 0}
                            onChange={(e) => setDraft(g._id, i, e.target.value)}
                            inputProps={{ "aria-label": `${k.title} — current` }}
                            sx={{ width: "6.5rem", "& .MuiInputBase-root": { fontSize: "0.875rem", borderRadius: "8px" } }}
                          />
                          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, whiteSpace: "nowrap" }}>
                            of {k.target ?? "—"} {k.unit || ""}
                          </Box>
                        </Stack>
                      ))}
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: "1.25rem", gap: 1.5, flexWrap: "wrap" }}>
                      <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted }}>
                        Your manager sees this as soon as you save it.
                      </Box>
                      <Stack direction="row" spacing={1.25}>
                        <BtnSecondary
                          onClick={() => {
                            setEditing(null);
                            setDrafts((p) => ({ ...p, [g._id]: {} }));
                            setCheckDrafts((p) => { const n = { ...p }; delete n[g._id]; return n; });
                          }}
                          sx={{ borderRadius: "8px" }}
                        >
                          Discard
                        </BtnSecondary>
                        <BtnPrimary onClick={() => save(g)} disabled={!dirty || saving === g._id} sx={{ borderRadius: "8px" }}>
                          {saving === g._id ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : "Save"}
                        </BtnPrimary>
                      </Stack>
                    </Stack>
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      )}

      <Stack direction={{ xs: "column", lg: "row" }} spacing={2.5} sx={{ mt: "1.25rem" }} alignItems="stretch">
        {review && (
          <MainBox sx={{ flex: 1, minWidth: 0, padding: "1.625rem" }}>
            <Section>Your review · {cycle?.name || "this cycle"}</Section>
            <Box sx={{ fontSize: "0.875rem", lineHeight: 1.7, color: tokens.text, mb: "1.125rem" }}>
              {review.status === "pending"
                ? "You and your manager each answer the same questions about this cycle. Neither of you sees the other's until both are in."
                : review.status === "self_completed"
                ? "Your self-review is in. Your manager's is next, and you will see both once the cycle is signed off."
                : review.status === "manager_completed"
                ? "Both reviews are in and the rating is waiting on sign-off."
                : "This review is closed."}
            </Box>
            <Stack direction="row" spacing={1.25} sx={{ flexWrap: "wrap", gap: 1.25 }}>
              <BtnPrimary sx={{ borderRadius: "8px" }} onClick={() => setReviewOpen(true)}>
                {review.status === "pending" ? "Start your self-review" : "Read your answers"}
              </BtnPrimary>
              {/* Competency scoring lives on the full page, where the scale and
                  the weights are. The modal is the written half only. */}
              {(cycle?.competencies || []).length > 0 && (
                <BtnSecondary sx={{ borderRadius: "8px" }} onClick={() => navigate(`/app/performance/review/${review._id}`)}>
                  Score competencies
                </BtnSecondary>
              )}
            </Stack>
          </MainBox>
        )}

        {previous && (
          <MainBox sx={{ flex: 1, minWidth: 0, padding: "1.625rem" }}>
            <Section>Last cycle · {previous.cycle?.name || "previous"}</Section>
            <Stack direction="row" alignItems="baseline" spacing={1.25} sx={{ mb: "0.5rem" }}>
              <Box sx={{ fontFamily: displayFont, fontSize: "1.375rem", fontWeight: 700, color: tokens.text }}>
                {previous.overallRating != null
                  ? `${previous.overallRating} / ${previous.cycle?.ratingScale?.max || 5}`
                  : "—"}
              </Box>
              <StatusChip label="Signed off" tone="success" />
            </Stack>
            <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, lineHeight: 1.6, mb: "1.125rem" }}>
              The rating agreed at calibration. It does not change once shared.
            </Box>
            <BtnSecondary sx={{ borderRadius: "8px" }} onClick={() => navigate(`/app/performance/review/${previous._id}`)}>
              Read the full review
            </BtnSecondary>
          </MainBox>
        )}
      </Stack>

      <SelfReviewModal
        open={reviewOpen}
        review={review}
        cycle={cycle}
        onClose={() => setReviewOpen(false)}
        onSubmitted={load}
      />
    </Box>
  );
};

export default MyGoals;
